import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AutomationStep } from "@/types/Automation";
import { COMMON_TIMEZONES, WAIT_UNIT_OPTIONS } from "../constants";
import type { StepNodeData, WaitMode } from "../types";
import { useDebouncedSyncedString } from "../useDebouncedSyncedString";
import { getValidTimeZone } from "../utils";

export function WaitStepFields({
  step,
  onStepDataChange,
  artistTimezone,
}: {
  step: AutomationStep;
  onStepDataChange: StepNodeData["onStepDataChange"];
  artistTimezone: string;
}) {
  const durationField = useDebouncedSyncedString({
    syncKey: `${step.id}-duration`,
    externalValue: step.data?.duration != null ? String(step.data.duration) : "",
    onCommit: ({ value }) => onStepDataChange({ stepId: step.id, field: "duration", value }),
  });

  const untilAtLocalRaw = step.data?.untilAtLocal;
  const untilAtLocalField = useDebouncedSyncedString({
    syncKey: `${step.id}-until`,
    externalValue: typeof untilAtLocalRaw === "string" ? untilAtLocalRaw : "",
    onCommit: ({ value }) => onStepDataChange({ stepId: step.id, field: "untilAtLocal", value }),
  });

  return (
    <div className="p-4 nodrag" onPointerDownCapture={(e) => e.stopPropagation()}>
      <div className="space-y-3">
        <Select
          value={(step.data?.mode as WaitMode) || "duration"}
          onValueChange={(val) => onStepDataChange({ stepId: step.id, field: "mode", value: val })}
        >
          <SelectTrigger className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="duration">Delay for</SelectItem>
            <SelectItem value="until">Delay until</SelectItem>
          </SelectContent>
        </Select>

        {((step.data?.mode as WaitMode) || "duration") === "duration" ? (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              placeholder="1"
              value={durationField.draft}
              onChange={durationField.onChange}
              onBlur={durationField.onBlur}
              onFocus={durationField.onFocus}
              className="w-20 text-sm"
            />
            <Select
              value={step.data?.unit || "minutes"}
              onValueChange={(val) => onStepDataChange({ stepId: step.id, field: "unit", value: val })}
            >
              <SelectTrigger className="flex-1 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WAIT_UNIT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              type="datetime-local"
              value={untilAtLocalField.draft}
              onChange={untilAtLocalField.onChange}
              onBlur={untilAtLocalField.onBlur}
              onFocus={untilAtLocalField.onFocus}
              className="text-sm"
            />
            <div>
              <div className="text-xs font-medium text-purple-700 mb-1.5">Timezone</div>
              <Select
                value={getValidTimeZone({ value: (step.data?.timezone as string) || artistTimezone })}
                onValueChange={(val) => onStepDataChange({ stepId: step.id, field: "timezone", value: val })}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([...COMMON_TIMEZONES, artistTimezone, (step.data?.timezone as string) || ""])]
                    .filter(Boolean)
                    .sort()
                    .map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
